import {groupByUniqueKey} from '@benchling/js-utils';
import React, {useMemo} from 'react';

import SelectDropdown, {SelectDropdownProps} from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import {OptionData} from 'ui-library/menus/MenuConstants';
import {immutableRemoveAt} from 'util/ImmutableListUtil';
import {ellipsize} from 'util/NameUtil';

import SearchableMenu, {SearchableMenuProps} from '../menus/SearchableMenu';

export interface SelectSearchableDropdownMenuProps
  extends Partial<
    Pick<
      SearchableMenuProps,
      | 'className'
      | 'dropLeft'
      | 'scrollable'
      | 'getMatchingOptionDatas'
      | 'height'
      | 'itemContainerClassName'
      | 'renderOptionContent'
      | 'style'
      | 'width'
      | 'footer'
      | 'isMulti'
    >
  > {}

export interface SelectSearchableDropdownProps
  extends Pick<
    SelectDropdownProps,
    | 'id'
    | 'buttonSize'
    | 'closeOnOutsideClick'
    | 'closeOnInsideClick'
    | 'disabled'
    | 'error'
    | 'errorStyle'
    | 'isOpen'
    | 'onToggle'
    | 'tooltipToggleDisplayMode'
    | 'className'
    | 'buttonClassName'
    | 'autoFocus'
    | 'alwaysDropDown'
    | 'dataTestAttrs'
  > {
  dropdownToggleOverride?: JSX.Element;
  menuProps?: SelectSearchableDropdownMenuProps;
  noneSelectedMessage: React.ReactNode;
  optionDatas: Array<OptionData>;
  onSelect: (selectedValues: Array<string>) => void;
  /**
   * How to show the selected values in the dropdown trigger.
   *
   * @default comma-separated concatenation of all selected option names (or values, if an option has no name)
   */
  renderSelectedValues?: (
    selectedValues: Array<string>,
    deselectValueAtIndex: (idx: number) => void
  ) => React.ReactNode;
  selectedValues: Array<string>;
}

export default function SelectSearchableDropdown(props: SelectSearchableDropdownProps): JSX.Element {
  // Options without values aren't selectable, but can still be present as headers. These must be removed
  // from the mapping to avoid non-unique key errors
  const optionDataByValue = useMemo(
    () =>
      groupByUniqueKey(
        props.optionDatas.filter(({value}) => value != null),
        ({value}) => value
      ),
    [props.optionDatas]
  );

  const deselectValueAtIndex = (selectedValueIdx: number): void => {
    props.onSelect(immutableRemoveAt(props.selectedValues, selectedValueIdx));
  };

  const renderSelectedValues = (selectedValues: Array<string>): React.ReactNode =>
    props.renderSelectedValues != null
      ? props.renderSelectedValues(selectedValues, deselectValueAtIndex)
      : ellipsize(selectedValues.map((value) => optionDataByValue[value]?.name ?? value).join(', '), 35);

  return (
    <SelectDropdown
      id={props.id}
      className={props.className}
      buttonSize={props.buttonSize}
      closeOnOutsideClick={props.closeOnOutsideClick}
      disabled={props.disabled}
      dropdownToggleOverride={props.dropdownToggleOverride}
      error={props.error}
      errorStyle={props.errorStyle}
      isOpen={props.isOpen}
      onToggle={props.onToggle}
      tooltipToggleDisplayMode={props.tooltipToggleDisplayMode}
      buttonClassName={props.buttonClassName}
      autoFocus={props.autoFocus}
      alwaysDropDown={props.alwaysDropDown}
      dataTestAttrs={props.dataTestAttrs}
      closeOnInsideClick={props.closeOnInsideClick ?? false}
      displayName={
        props.selectedValues.length > 0
          ? renderSelectedValues(props.selectedValues)
          : props.noneSelectedMessage
      }
      menu={
        <SearchableMenu
          width={Menu.WIDTHS.FULL}
          {...props.menuProps}
          selectedValue={props.selectedValues}
          scrollable={true}
          onSelect={(selectedValue) => {
            const selectedValueIdx = props.selectedValues.indexOf(selectedValue);
            if (selectedValueIdx >= 0) {
              deselectValueAtIndex(selectedValueIdx);
            } else {
              props.onSelect([...props.selectedValues, selectedValue]);
            }
          }}
          optionDatas={props.optionDatas}
          skipIconOverride={true}
        />
      }
    />
  );
}
